import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';

type FilterType = 'all' | 'in-inventory' | 'latest';

@Component({
  selector: 'app-book-catalogue',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './book-catalogue.component.html',
  styleUrl: './book-catalogue.component.scss',
})
export class BookCatalogueComponent implements OnDestroy {
  filter: FilterType = 'all';
  latestCount = 50;
  isLoading = false;
  loadingMessage = '';
  successMessage = '';
  errorMessage = '';
  returnTo: string | null = null;

  readonly filterOptions: { value: FilterType; label: string; description: string }[] = [
    {
      value: 'all',
      label: 'All Books',
      description: 'Every book in the catalogue, sorted alphabetically.',
    },
    {
      value: 'in-inventory',
      label: 'Books with Physical Copies',
      description: 'Only books that have at least one copy in inventory.',
    },
    {
      value: 'latest',
      label: 'Latest Additions',
      description: 'Books whose copies were most recently added to inventory.',
    },
  ];

  // Purely cosmetic status text while we poll the server-side job — generation now runs
  // in a background worker thread decoupled from any single HTTP request, so there's no
  // request timeout to race against; these messages just keep the wait from feeling stuck.
  private readonly _PROGRESS: { delay: number; msg: string }[] = [
    { delay: 0,       msg: 'Fetching book data from database…' },
    { delay: 5_000,   msg: 'Building PDF layout…' },
    { delay: 15_000,  msg: 'Rendering pages — this may take a moment for large catalogues…' },
    { delay: 35_000,  msg: 'Still rendering — large catalogues take longer…' },
    { delay: 60_000,  msg: 'Processing complete catalogue — please wait…' },
    { delay: 90_000,  msg: 'Still working — very large catalogues can take several minutes…' },
    { delay: 130_000, msg: 'Still working — feel free to keep this tab open, no need to retry…' },
  ];

  private readonly _POLL_INTERVAL_MS = 3_000;
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;

  private _timers: ReturnType<typeof setTimeout>[] = [];
  private _successTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private api: ApiService, private pageTitle: PageTitleService, private router: Router) {
    this.pageTitle.set('Book Catalogue', 'Generate a PDF catalogue');
    this.returnTo = history.state?.returnTo || null;
  }

  goBack(): void {
    this.router.navigateByUrl(this.returnTo!);
  }

  ngOnDestroy(): void {
    this._clearTimers();
  }

  generate(): void {
    if (this.filter === 'latest' && (!this.latestCount || this.latestCount < 1 || this.latestCount > 2000)) {
      this.errorMessage = 'Please enter a count between 1 and 2000.';
      return;
    }

    this._clearTimers();
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.loadingMessage = this._PROGRESS[0].msg;

    // Schedule message rotation
    this._timers = this._PROGRESS.slice(1).map(step =>
      setTimeout(() => { this.loadingMessage = step.msg; }, step.delay)
    );

    const count = this.filter === 'latest' ? this.latestCount : undefined;

    this.api.startBookCataloguePdf(this.filter, count).subscribe({
      next: ({ jobId }) => this._pollJob(jobId),
      error: (err: any) => this._fail(err?.message || 'Failed to start PDF generation.'),
    });
  }

  // The generation job runs server-side in a background worker thread, independent of
  // any single HTTP request's lifetime — so we poll for completion rather than waiting
  // on one long-lived request that an upstream proxy/gateway could time out.
  private _pollJob(jobId: string): void {
    this._pollTimer = setTimeout(() => {
      this.api.getBookCataloguePdfStatus(jobId).subscribe({
        next: (res) => {
          if (res.status === 'processing') { this._pollJob(jobId); return; }
          if (res.status === 'error')      { this._fail(res.error || 'Failed to generate PDF.'); return; }
          this._downloadResult(jobId);
        },
        error: (err: any) => this._fail(err?.message || 'Failed to check PDF status.'),
      });
    }, this._POLL_INTERVAL_MS);
  }

  private _downloadResult(jobId: string): void {
    this.api.downloadBookCataloguePdfResult(jobId).subscribe({
      next: (blob: Blob) => {
        this._clearTimers();
        this.isLoading = false;
        this.loadingMessage = '';

        const label    = this.filter === 'in-inventory' ? 'In-Inventory'
                       : this.filter === 'latest'        ? `Latest-${this.latestCount}`
                       : 'All-Books';
        const date     = new Date().toISOString().slice(0, 10);
        const filename = `BookCatalogue_${label}_${date}.pdf`;

        const url = URL.createObjectURL(blob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 30_000);

        this.successMessage = filename;
        // Auto-clear success banner after 30 s
        this._successTimer = setTimeout(() => { this.successMessage = ''; }, 30_000);
      },
      error: (err: any) => {
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result as string);
              this._fail(json.errorDescription || 'Failed to download PDF.');
            } catch {
              this._fail('Failed to download PDF. Please try again.');
            }
          };
          reader.readAsText(err.error);
        } else {
          this._fail(err?.message || 'Failed to download PDF. Please try again.');
        }
      },
    });
  }

  private _fail(message: string): void {
    this._clearTimers();
    this.isLoading = false;
    this.loadingMessage = '';
    this.errorMessage = message;
  }

  dismissSuccess(): void {
    this.successMessage = '';
    if (this._successTimer) clearTimeout(this._successTimer);
  }

  private _clearTimers(): void {
    this._timers.forEach(t => clearTimeout(t));
    this._timers = [];
    if (this._pollTimer)    { clearTimeout(this._pollTimer);    this._pollTimer = null; }
    if (this._successTimer) { clearTimeout(this._successTimer); this._successTimer = null; }
  }
}
